import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPublicUserProfile, type UserProfile } from '@/lib/coreApi';
import ProfilePreview from '@/components/profiles/ProfilePreview';
import type { ProfileLink } from '@/components/profiles/ProfileLinkEditor';
import type { ProfileFeedEntry } from '@/components/profiles/ProfileFeedEditor';

export default function UserProfilePage() {
  const { handle } = useParams<{ handle: string }>();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!handle) return;
    setLoading(true);
    setError(null);
    getPublicUserProfile(handle)
      .then((res) => setProfile(res.data))
      .catch((err) => setError(err.message || 'Profile not found'))
      .finally(() => setLoading(false));
  }, [handle]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-cocoa/20 border-t-cocoa" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 text-cocoa/60">
        <p className="text-lg font-medium">Profile not found</p>
        <a href="/" className="text-sm underline hover:text-cocoa">Back to home</a>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <ProfilePreview
        displayName={profile.display_name || handle || 'User'}
        tagline={profile.tagline || ''}
        quoteText={profile.quote_text || ''}
        quoteAttribution={profile.quote_attr || ''}
        themeKey={profile.theme || 'sunrise'}
        avatarUrl={profile.avatar_url}
        coverUrl={profile.cover_url}
        links={(profile.links as ProfileLink[]) || []}
        feedEntries={(profile.feed_entries as ProfileFeedEntry[]) || []}
        role={profile.role}
      />

      {profile.bio_md && (
        <section className="mt-8 rounded-2xl border border-sand/50 bg-white/70 p-6">
          <h2 className="mb-3 text-lg font-semibold text-cocoa">About</h2>
          <div className="prose prose-cocoa max-w-none text-sm leading-relaxed whitespace-pre-wrap">
            {profile.bio_md}
          </div>
        </section>
      )}

      {/* Share button */}
      <div className="mt-6 flex justify-center">
        <button
          type="button"
          className="rounded-xl border border-sand px-4 py-2 text-sm font-medium text-cocoa transition hover:border-cocoa"
          onClick={() => {
            navigator.clipboard.writeText(window.location.href);
          }}
        >
          Copy profile link
        </button>
      </div>
    </div>
  );
}
